export interface DataKitDto {
  id?: string;
  teamId?: string;
  name: string;
  description: string;
  archived: boolean;
  itemCount: number;
  locationCount: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface DataKitPriceOverrideDto {
  id?: string;
  unitType: string;
  unitId: string;
  price: string;
}

/** One menu's price for one size. [menuId] is the menu's design id (Oct 12: every design is a menu). */
export interface DataKitMenuPriceDto {
  menuId: string;
  price: string;
}

export interface DataKitItemPriceDto {
  id?: string;
  size: string;
  price: string;
  sequence: number;
  overrides: DataKitPriceOverrideDto[];
  /** Per-menu prices for this size. Read-only here; writes go through the menu-price route. */
  menuOverrides?: DataKitMenuPriceDto[];
}

export interface DataKitItemGroupDto {
  groupName: string;
  /** Full ancestry, root first: ["Prime Seafood", "Raw"]. Replaced subgroupName (Sept 16). */
  groupPath: string[];
}

/*
 * Menus come from designs (Oct 12): every live design placing at least one connected library item
 * is a menu, named after the design. A menu's id is its design id.
 */

export interface MenuLibraryDto {
  id: string;
  name: string;
}

/** One menu (design) - Item Libraries › Menus, and a library's Menus tab. */
export interface MenuSummaryDto {
  designId: string;
  name: string;
  locationId?: number | null;
  locationName?: string | null;
  libraries: MenuLibraryDto[];
  /** Library items it places (on a library's tab: that library's items). */
  itemCount: number;
  /** Menu prices set for it - item sizes and modifier options together. */
  menuPriceCount: number;
}

export interface ItemMenuPriceDto {
  priceId?: string | null;
  size: string;
  price: string;
  /** MENU, LOCATION or LIBRARY - where the price this menu shows comes from. */
  tier: string;
}

/** A menu showing one item - "Used on N menus", opened, and the menu price picker. */
export interface ItemMenuDto {
  designId: string;
  name: string;
  locationId?: number | null;
  locationName?: string | null;
  prices: ItemMenuPriceDto[];
}

/** One menu price - of an item size, or of a modifier option. */
export interface MenuPriceRowDto {
  kitId: string;
  itemId: string;
  itemExternalId: string;
  itemName: string;
  priceId?: string | null;
  size?: string | null;
  modifierId?: string | null;
  modifierName?: string | null;
  modifierGroupName?: string | null;
  price: string;
}

/** A menu's read-only view. */
export interface MenuDetailDto {
  designId: string;
  name: string;
  locationId?: number | null;
  locationName?: string | null;
  libraries: MenuLibraryDto[];
  itemCount: number;
  menuPrices: MenuPriceRowDto[];
}

export interface MenuRefDto {
  designId: string;
  name: string;
}

/** A group's "Used on" line - the menus placing any of its items. */
export interface GroupMenusDto {
  groupName: string;
  menus: MenuRefDto[];
}

/*
 * Review changes (Oct 12): everything waiting to publish, field by field, with who, when and where.
 */

export interface WaitingChangeDto {
  changeKey: string;
  field: string;
  /** "Name", "Price · Large", "Dallas, TX price", "Blackened · Happy Hour menu price"... */
  label: string;
  oldValue?: string | null;
  newValue?: string | null;
  /** Shown as "Removed" - a menu or location price taken away. */
  removed: boolean;
  editedById?: string | null;
  editedByName?: string | null;
  editedDate?: string | null;
  /** LIBRARY_PAGE, TIMBER or IMPORT. */
  source?: string | null;
  /** "Library page", "Timber", "Import · Spring_Menu_v3.xlsx". */
  sourceLabel?: string | null;
  fileName?: string | null;
  originDesignId?: string | null;
  originDesignName?: string | null;
  designId?: string | null;
  unitId?: string | null;
}

export interface WaitingItemDto {
  /** ITEM or GROUP - a group's own name/note/position change is a row of its own. */
  kind: "ITEM" | "GROUP" | string;
  itemId?: string | null;
  itemExternalId?: string | null;
  groupId?: string | null;
  name: string;
  groupPaths: string[];
  lastChangedDate?: string | null;
  changes: WaitingChangeDto[];
}

export interface ReviewChangesDto {
  itemCount: number;
  totalChanges: number;
  items: WaitingItemDto[];
}

export interface MenuPriceRequestDto {
  menuId: string;
  size: string;
  price?: string | null;
  /** The price row - size alone can't tell two unlabelled prices apart. */
  priceId?: string | null;
}

export interface VenueMenuPriceDto {
  menuId: string;
  menuName: string;
  price: string;
}

export interface ReparentGroupRequestDto {
  path: string[];
  newParentPath: string[];
}

export interface SubgroupRequestDto {
  groupName: string;
  name: string;
  /** Full path to create, root first. Falls back to name as one level when empty. */
  path: string[];
}

/** One modifier option price at a location ([unitId] = location id) or on a menu ([unitId] = design id). */
export interface DataKitModifierOverrideDto {
  unitId: string;
  price: string;
}

export interface DataKitItemModifierDto {
  id?: string;
  name: string;
  /** The library price. */
  price?: string | null;
  sequence: number;
  /**
   * This option's location prices (Oct 12) - set in the drawer's location price table. Left out on
   * a write, the server keeps them as they are; a list replaces them.
   */
  overrides?: DataKitModifierOverrideDto[] | null;
  /** This option's menu (design) prices - read-only; they are set from Timber. */
  menuOverrides?: DataKitModifierOverrideDto[];
}

export interface DataKitItemModifierGroupDto {
  id?: string;
  name: string;
  sequence: number;
  modifiers: DataKitItemModifierDto[];
}

export interface DataKitSourceEventDto {
  id?: string;
  kind: string;
  fileName?: string | null;
  occurredDate?: string;
}

export interface DataKitItemDto {
  id?: string;
  externalId?: string;
  dataKitId?: string;
  teamId?: string;
  name: string;
  description: string;
  graphicRef?: string | null;
  sequence: number;
  archived: boolean;
  available: boolean;
  extras?: string | null;
  prices: DataKitItemPriceDto[];
  tags: string[];
  /** Dietary / Allergen labels (Oct 14), apart from tags. Omitted on a write leaves them as they are. */
  dietary?: string[] | null;
  groups: DataKitItemGroupDto[];
  modifierGroups: DataKitItemModifierGroupDto[];
  sourceEvent?: DataKitSourceEventDto | null;
  usedOnCount: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface DataKitGroupDto {
  id?: string;
  /** Full ancestry, root first, when the server sends it. */
  path?: string[];
  dataKitId?: string;
  name: string;
  note?: string | null;
  itemCount: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface DataKitTagDto {
  id?: string;
  dataKitId?: string;
  name: string;
  itemCount: number;
  createdDate?: string;
  updatedDate?: string;
}

/** What deleting a kit would reach - every location it's on, and how many designs there use it. */
export interface KitDeletePreviewDto {
  kitId: string;
  itemCount: number;
  locations: { unitType: string; unitId: string; designCount: number }[];
}

export interface DataKitDeleteResultDto {
  itemsDeleted: number;
  locationsRemoved: number;
  designsUnlinked: number;
}

export interface DataKitAssignmentDto {
  id?: string;
  dataKitId?: string;
  unitType: string;
  unitId: string;
}

export interface MembershipRequestDto {
  groupName: string;
}

export interface SetAvailabilityRequestDto {
  itemIds: string[];
  available: boolean;
}

export interface BulkItemIdsRequestDto {
  itemIds: string[];
}

export interface RenameGroupRequestDto {
  name: string;
  newName: string;
  path: string[];
}

export interface GroupNoteRequestDto {
  name: string;
  note?: string | null;
  path: string[];
}

export interface RenameTagRequestDto {
  name: string;
  newName: string;
}

export interface MergeTagsRequestDto {
  from: string;
  into: string;
}

export interface UnassignPreviewDto {
  kitId: string;
  unitType: string;
  unitId: string;
  itemCount: number;
  designCount: number;
  placedItemCount: number;
}

export interface DataKitItemDiffDto {
  itemId: string;
  externalId: string;
  name: string;
  new: boolean;
  changedFields: string[];
}

export interface DataKitGroupDiffDto {
  groupId: string;
  name: string;
  new: boolean;
  changedFields: string[];
}

export interface DataKitPublishDiffDto {
  /** itemCount + groupCount - items and groups, not fields. */
  totalChanges: number;
  /** Items on a design with at least one change waiting. */
  itemCount?: number;
  /** Groups a design shows whose own name, note or position has a change waiting. */
  groupCount?: number;
  /** Distinct designs that will change when this publishes. */
  designCount?: number;
  items: DataKitItemDiffDto[];
  groups: DataKitGroupDiffDto[];
}

export interface DataKitPublishResultDto {
  itemsPublished: number;
  groupsPublished: number;
}

export interface DataKitDiscardResultDto {
  itemsReverted: number;
  itemsDeleted: number;
  groupsReverted: number;
}

export interface DataKitPublishEventDto {
  id?: string;
  designId: string;
  itemExternalId: string;
  field: string;
  oldValue?: string | null;
  newValue?: string | null;
  applied: boolean;
  publishedDate?: string;
}

export interface VenuePriceDto {
  size: string;
  price: string;
  overridden: boolean;
  /** Menus that price this size themselves - drives the "every menu except Dinner" chip. */
  menuPrices: VenueMenuPriceDto[];
}

export interface VenueGroupEntryDto {
  groupName: string;
  groupNote: string;
  /** Full published ancestry, root first - one design header per element. */
  groupPath: string[];
}

export interface VenueItemDto {
  externalId: string;
  dataKitId: string;
  kitName: string;
  name: string;
  description: string;
  groupName: string;
  groupNote: string;
  groups: VenueGroupEntryDto[];
  tags: string[];
  graphicRef?: string | null;
  prices: VenuePriceDto[];
  available: boolean;
}

export interface ColumnSuggestion {
  columnIndex: number;
  header: string;
  target: string;
}

export interface ImportParseDto {
  fileName: string;
  headers: string[];
  rows: string[][];
  rowNumbers: number[];
  suggestedMapping: ColumnSuggestion[];
  detectedPairs: number;
}

export interface ImportMappingDto {
  columnIndex: number;
  target: string;
  pairIndex?: number | null;
}

export interface ImportRowPriceDto {
  size: string;
  price: string;
}

export interface ImportRowModifierDto {
  name: string;
  price?: string | null;
}

export interface ImportRowModifierGroupDto {
  name: string;
  modifiers: ImportRowModifierDto[];
}

export interface ImportRowDto {
  rowNumber: number;
  name: string;
  /** The leaf group the row is filed in. */
  groupName: string;
  /** Full ancestry, root first. Empty falls back to [groupName]. */
  groupPath?: string[];
  /**
   * Further groups the same item is filed in - set by the server when it folds rows sharing an
   * external id and differing only by group into one item.
   */
  alsoInGroupPaths?: string[][];
  /** Rows folded into this one, so skipping it can drop them as well. */
  foldedRowNumbers?: number[];
  description?: string | null;
  extras?: string | null;
  tags?: string[] | null;
  /** Dietary / Allergen labels (Oct 14) - null when no Dietary / Allergen column was mapped. */
  dietary?: string[] | null;
  externalId: string;
  prices?: ImportRowPriceDto[] | null;
  /** null when no modifier column was mapped; an empty array means the member cleared them. */
  modifierGroups?: ImportRowModifierGroupDto[] | null;
  matchedItemId?: string | null;
}

/** What kind of decision an ambiguous row is - see shared's `ImportAmbiguousRowDto.kind`. */
export type ImportDecisionKind =
  | "FILE_DUPLICATE"
  | "KIT_MATCH"
  | "EXTERNAL_ID_CONFLICT"
  | "ARCHIVED_ID"
  | "UPDATE_COLLISION";

export interface ImportAmbiguousRowDto {
  row: ImportRowDto;
  reason: string;
  candidateItemIds: string[];
  /** True when this row can be resolved by adding it as its own new item. */
  canCreateSeparately?: boolean;
  kind?: ImportDecisionKind;
  /** Rows sharing this key are one decision card. */
  setKey?: string;
  /** FILE_DUPLICATE only: what keeping the row does. */
  outcome?: "NEW" | "UPDATE" | "UP_TO_DATE" | "CHOOSE" | null;
}

/** The `resolutions` value meaning "add this row as a separate new item". */
export const IMPORT_RESOLUTION_CREATE = "__create__";
/** The `resolutions` value meaning the member chose to skip this row. */
export const IMPORT_RESOLUTION_SKIP = "__skip__";
/** The `resolutions` value meaning "keep this row as it is" - a FILE_DUPLICATE row's default. */
export const IMPORT_RESOLUTION_KEEP = "__keep__";

export interface SkippedRowDto {
  rowNumber: number;
  reason: string;
}

export interface DataKitImportRequestDto {
  headers: string[];
  rows: string[][];
  rowNumbers: number[];
  mapping: ImportMappingDto[];
  fileName: string;
  resolutions: Record<number, string>;
}

export interface DataKitImportPreviewDto {
  creates: ImportRowDto[];
  updates: ImportRowDto[];
  ambiguous: ImportAmbiguousRowDto[];
  /** Matched rows that would change nothing - counted, not imported again. */
  alreadyUpToDate?: ImportRowDto[];
  /** Byte-identical repeats of an earlier row, collapsed before matching. */
  mergedDuplicates?: ImportRowDto[];
  skipped: SkippedRowDto[];
}

export interface DataKitImportResultResponseDto {
  created: number;
  updated: number;
  skipped: number;
  ambiguous: number;
  /** Rows the member chose to skip in the review step. */
  skippedByChoice?: number;
  ambiguousRows: ImportAmbiguousRowDto[];
  skippedRows: SkippedRowDto[];
}

export interface VenuePriceOverrideRequestDto {
  size: string;
  price?: string | null;
}
